import React, { useEffect, useRef, useState } from "react";
import { FiAlertTriangle, FiWifi, FiZap } from "react-icons/fi";

/**
 * CameraStreamWebRTC - Stable Version
 * ✅ Auto-reconnects on temporary disconnections
 * ✅ Keepalive to prevent idle drops
 * ✅ Better connection state handling
 * ✅ Prevents unnecessary cleanup
 * ✅ React.memo to prevent re-renders
 * ✅ Respects is_active flag (inactive cameras don't connect)
 * ✅ Silent reconnections (no UI spam)
 */
function CameraStreamWebRTC({
  path, // e.g. "cam16"
  baseUrl = "https://vision.viewmax.net",
  className = "",
  muted = true,
  autoplay = true,
  isActive = true,           // ✅ NEW: Camera active status
  isOnline = true,           // ✅ NEW: Camera online status
  cameraName = "",           // ✅ NEW: For logging
  webrtcTimeoutMs = 10000,
  frameTimeoutMs = 5000,
  maxRetries = 3,
  retryDelayMs = 2000,
  reconnectDelayMs = 3000,
  maxReconnects = 999,
  keepaliveIntervalMs = 30000,
  onFallback,
  showControls = false,
}) {
  const videoRef = useRef(null);
  const pcRef = useRef(null);
  const abortRef = useRef(null);
  const timeoutRef = useRef(null);
  const frameTimerRef = useRef(null);
  const reconnectTimerRef = useRef(null);
  const keepaliveRef = useRef(null);
  const reconnectCountRef = useRef(0);
  const isUnmountedRef = useRef(false);

  const [status, setStatus] = useState("connecting");
  const [info, setInfo] = useState("Connecting…");

  const [isMuted, setIsMuted] = useState(true);

  const clearTimers = () => {
    if (timeoutRef.current) clearTimeout(timeoutRef.current);
    if (frameTimerRef.current) clearTimeout(frameTimerRef.current);
    if (reconnectTimerRef.current) clearTimeout(reconnectTimerRef.current);
    if (keepaliveRef.current) clearInterval(keepaliveRef.current);
    timeoutRef.current = null;
    frameTimerRef.current = null;
    reconnectTimerRef.current = null;
    keepaliveRef.current = null;
  };

  const cleanup = (preserveVideo = false) => {
    clearTimers();

    try {
      abortRef.current?.abort();
    } catch {}
    abortRef.current = null;

    try {
      if (pcRef.current) {
        pcRef.current.ontrack = null;
        pcRef.current.onconnectionstatechange = null;
        pcRef.current.oniceconnectionstatechange = null;
        pcRef.current.close();
      }
    } catch {}
    pcRef.current = null;

    // ✅ NEW: Optionally preserve video element during reconnect
    if (!preserveVideo) {
      try {
        if (videoRef.current) {
          videoRef.current.pause();
          videoRef.current.srcObject = null;
        }
      } catch {}
    }
  };

  const sleep = (ms) => new Promise((r) => setTimeout(r, ms));

  /** POST SDP to WHEP, preserving POST across redirects */
  const postOfferToWhep = async (url, sdp, signal) => {
    const res = await fetch(url, {
      method: "POST",
      headers: { "Content-Type": "application/sdp" },
      body: sdp,
      signal,
      redirect: "manual",
    });

    if ([301, 302, 307, 308].includes(res.status)) {
      const loc = res.headers.get("Location");
      if (!loc) throw new Error("WHEP redirect without Location header");

      const nextUrl = loc.startsWith("http")
        ? loc
        : `${baseUrl.replace(/\/$/, "")}${loc.startsWith("/") ? "" : "/"}${loc}`;

      return postOfferToWhep(nextUrl, sdp, signal);
    }

    const text = await res.text();

    if (text.includes("<html")) {
      throw new Error("WHEP returned HTML (proxy/routing issue)");
    }

    if (!res.ok) {
      throw new Error(`WHEP HTTP ${res.status}: ${text.slice(0, 200)}`);
    }

    if (!text.trim().startsWith("v=")) {
      throw new Error("WHEP did not return SDP answer");
    }

    return text;
  };

  /** Wait until real video frames exist */
  const waitForFrames = (video) =>
    new Promise((resolve, reject) => {
      const start = Date.now();

      const check = () => {
        if (!video || isUnmountedRef.current) return reject();

        if (
          video.readyState >= 2 &&
          video.videoWidth > 0 &&
          !video.paused
        ) {
          return resolve();
        }

        if (Date.now() - start > frameTimeoutMs) {
          return reject(new Error("No decoded frames"));
        }

        requestAnimationFrame(check);
      };

      check();
    });

  /** ✅ NEW: Start keepalive to prevent idle disconnects */
  const startKeepalive = (pc) => {
    if (keepaliveRef.current) clearInterval(keepaliveRef.current);
    
    keepaliveRef.current = setInterval(() => {
      if (!pc || pc.connectionState !== "connected") return;
      
      // Check if connection is still alive by monitoring stats
      pc.getStats().then(stats => {
        console.log(`📊 Camera ${path}: Connection alive, ${stats.size} stats`);
      }).catch(err => {
        console.warn(`⚠️ Camera ${path}: Stats check failed`, err);
      });
    }, keepaliveIntervalMs);
  };

  /** ✅ NEW: Reconnect logic for temporary disconnections */
  const scheduleReconnect = (reason) => {
    if (isUnmountedRef.current) return;
    
    reconnectCountRef.current++;
    
    if (reconnectCountRef.current > maxReconnects) {
      console.error(`❌ Camera ${path}: Max reconnects (${maxReconnects}) reached`);
      setStatus("failed");
      setInfo("Connection failed - too many retries");
      onFallback?.();
      return;
    }

    console.log(`🔄 Camera ${path}: Reconnecting (${reconnectCountRef.current}/${maxReconnects}) - ${reason}`);
    setStatus("connecting");
    setInfo(`Reconnecting... (${reconnectCountRef.current})`);

    reconnectTimerRef.current = setTimeout(() => {
      if (isUnmountedRef.current) return;
      start();
    }, reconnectDelayMs);
  };

  const start = async () => {
    if (isUnmountedRef.current) return;
    
    // ✅ NEW: Don't connect if camera is inactive
    if (!isActive) {
      console.log(`🚫 Camera ${path}: Inactive, not connecting`);
      setStatus("inactive");
      setInfo("Camera Inactive");
      return;
    }
    
    cleanup(true); // ✅ Preserve video during reconnect
    setStatus("connecting");
    setInfo("Starting WebRTC…");

    const whepUrl = `${baseUrl.replace(/\/$/, "")}/${path}/whep`;

    let attempt = 0;

    while (attempt <= maxRetries && !isUnmountedRef.current) {
      attempt++;
      setInfo(`WebRTC attempt ${attempt}/${maxRetries + 1}…`);

      const pc = new RTCPeerConnection({
        iceServers: [],  // Local network only
        bundlePolicy: 'max-bundle',
        rtcpMuxPolicy: 'require',
      });
      pcRef.current = pc;

      pc.addTransceiver("video", { direction: "recvonly" });
      pc.addTransceiver("audio", { direction: "recvonly" });  // ✅ Add audio

      abortRef.current = new AbortController();

      timeoutRef.current = setTimeout(() => {
        if (isUnmountedRef.current) return;
        console.warn(`⏱️ Camera ${path}: Connection timeout`);
        cleanup(true);
        scheduleReconnect("timeout");
      }, webrtcTimeoutMs);

      // ✅ IMPROVED: Better connection state handling
      pc.onconnectionstatechange = () => {
        console.log(`📡 Camera ${path}: Connection state: ${pc.connectionState}`);
        
        if (pc.connectionState === "connected") {
          console.log(`✅ Camera ${path}: WebRTC connected`);
          reconnectCountRef.current = 0; // Reset reconnect counter on success
          startKeepalive(pc);
        } else if (pc.connectionState === "disconnected") {
          // ✅ Temporary disconnection - try to reconnect
          console.warn(`⚠️ Camera ${path}: Temporarily disconnected`);
          clearTimers();
          scheduleReconnect("disconnected");
        } else if (pc.connectionState === "failed") {
          // ✅ Permanent failure - reconnect
          console.error(`❌ Camera ${path}: Connection failed`);
          clearTimers();
          scheduleReconnect("failed");
        } else if (pc.connectionState === "closed") {
          console.log(`🛑 Camera ${path}: Connection closed`);
          clearTimers();
        }
      };

      // ✅ NEW: Monitor ICE connection state too
      pc.oniceconnectionstatechange = () => {
        console.log(`🧊 Camera ${path}: ICE state: ${pc.iceConnectionState}`);
      };

      pc.ontrack = async (ev) => {
        if (isUnmountedRef.current) return;
        
        const video = videoRef.current;
        if (!video) return;

        console.log(`🎬 Camera ${path}: Received ${ev.track.kind} track`);

        const stream = ev.streams?.[0] || new MediaStream([ev.track]);

        video.srcObject = stream;
        video.muted = muted;
        video.playsInline = true;
        video.autoplay = autoplay;

        try {
          await video.play();
          await waitForFrames(video);

          clearTimers();
          setStatus("playing");
          setInfo("Live");
          console.log(`✅ Camera ${path}: Playing successfully`);
        } catch (err) {
          console.warn(`⚠️ Camera ${path}: Play failed:`, err);
          scheduleReconnect("play failed");
        }
      };

      try {
        const offer = await pc.createOffer();
        await pc.setLocalDescription(offer);

        const answer = await postOfferToWhep(
          whepUrl,
          offer.sdp,
          abortRef.current.signal
        );

        await pc.setRemoteDescription({ type: "answer", sdp: answer });
        
        // Wait a bit to see if connection succeeds
        await sleep(1000);
        
        if (!isUnmountedRef.current && pc.connectionState === "connected") {
          return; // Success!
        }
        
      } catch (err) {
        console.warn(`⚠️ Camera ${path}: Attempt ${attempt} failed:`, err.message);
        cleanup(true);

        if (attempt > maxRetries) {
          scheduleReconnect("max attempts reached");
          return;
        }

        await sleep(retryDelayMs);
      }
    }
  };

  useEffect(() => {
    isUnmountedRef.current = false;
    reconnectCountRef.current = 0;
    
    console.log(`🚀 Camera ${path} (${cameraName}): Initializing, isActive=${isActive}`);
    start();

    return () => {
      console.log(`🛑 Camera ${path}: Unmounting, cleaning up`);
      isUnmountedRef.current = true;
      cleanup(false);
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [baseUrl, path, isActive]);  // ✅ Restart when isActive changes

  return (
    <div className={`relative w-full h-full bg-black ${className}`}>
      <video
        ref={videoRef}
        className="absolute inset-0 w-full h-full object-contain"
        muted={true}
        playsInline
        autoPlay
	controls={showControls}
      />


      {/* ✅ INACTIVE: Show clear inactive message */}
      {status === "inactive" && (
        <div className="absolute inset-0 flex items-center justify-center bg-black/80 backdrop-blur-sm">
          <div className="text-center text-white px-4">
            <div className="w-12 h-12 rounded-full bg-gray-700 flex items-center justify-center mx-auto mb-3">
              <svg className="w-6 h-6" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M18.364 18.364A9 9 0 005.636 5.636m12.728 12.728A9 9 0 015.636 5.636m12.728 12.728L5.636 5.636" />
              </svg>
            </div>
            <div className="text-sm font-medium">Camera Inactive</div>
            <div className="text-xs text-gray-400 mt-1">Disabled by user</div>
          </div>
        </div>
      )}

      {/* ✅ CONNECTING: Only show on FIRST connection attempt, not reconnects */}
      {status === "connecting" && reconnectCountRef.current === 0 && (
        <div className="absolute inset-0 flex items-center justify-center bg-black/80 backdrop-blur-sm">
          <div className="text-center text-white px-4">
            <div className="w-12 h-12 border-4 border-blue-500 border-t-transparent rounded-full animate-spin mx-auto mb-3" />
            <div className="text-sm font-medium">Connecting...</div>
          </div>
        </div>
      )}

      {/* ✅ FAILED: Only show after giving up, not during reconnect attempts */}
      {status === "failed" && (
        <div className="absolute inset-0 flex items-center justify-center bg-black/80 backdrop-blur-sm">
          <div className="text-center text-white px-4">
            <FiAlertTriangle className="w-12 h-12 mx-auto mb-3 text-red-500" />
            <div className="text-sm font-medium">Connection Failed</div>
            <div className="text-xs text-gray-400 mt-1">Camera offline</div>
          </div>
        </div>
      )}

      {/* ✅ RECONNECTING: Subtle indicator, doesn't block video */}
      {status === "connecting" && reconnectCountRef.current > 0 && (
        <div className="absolute top-2 right-2 z-10">
          <div className="px-2 py-1 rounded-md text-xs font-semibold text-white bg-yellow-600/80 backdrop-blur-sm flex items-center gap-1.5">
            <div className="w-1.5 h-1.5 rounded-full bg-white animate-pulse" />
            Reconnecting
          </div>
        </div>
      )}

      {/* ✅ LIVE: Show when playing */}

    </div>
  );
}

// ✅ CRITICAL: Prevent re-renders that destroy WebRTC connection!
export default React.memo(CameraStreamWebRTC, (prevProps, nextProps) => {
  // Only re-render if these change
  return (
    prevProps.path === nextProps.path &&
    prevProps.baseUrl === nextProps.baseUrl &&
    prevProps.isActive === nextProps.isActive  // ✅ Re-render if active status changes
  );
});
